import { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, BOOKING_PATH } from "@/lib/site";
import { useLang } from "@/i18n/LanguageContext";
import { LanguageToggle } from "./LanguageToggle";
import { trackClick } from "@/lib/track";

export const Navbar = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "py-2" : "py-4"}`}
      data-testid="navbar"
    >
      <div className="mx-auto max-w-7xl px-5">
        <nav
          className={`flex items-center justify-between rounded-2xl px-4 sm:px-5 h-14 transition-all duration-500 ${
            scrolled ? "glass shadow-[0_20px_50px_-30px_rgba(0,0,0,0.9)]" : "border border-transparent"
          }`}
        >
          {/* Brand */}
          <a href="#top" className="flex items-center gap-3 group shrink-0" data-testid="brand-logo">
            <span className="relative grid place-items-center h-9 w-9 rounded-lg panel overflow-hidden">
              <span className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(61,123,255,0.6),transparent_60%)]" />
              <span className="font-display font-bold text-sm text-white relative">RA</span>
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-display font-semibold text-white text-sm tracking-tight">
                ReverseAI<span className="text-[var(--green)]">.dk</span>
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--muted-2)]">Secure Systems &amp; AI</span>
            </span>
          </a>

          {/* Desktop links */}
          <div className="hidden lg:flex items-center gap-5 xl:gap-6">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="text-sm text-muted hover:text-white transition-colors duration-300 whitespace-nowrap"
                data-testid={`nav-link-${l.href.replace("#", "")}`}
              >
                {t.nav[l.key]}
              </a>
            ))}
          </div>

          <div className="flex items-center gap-2.5 shrink-0">
            <LanguageToggle className="hidden sm:inline-flex" />
            <span className="hidden xl:inline-flex">
              <a
                href={`${BOOKING_PATH}?src=navbar`}
                onClick={() => trackClick("book_time")}
                className="btn-primary !py-2.5 !px-4 text-sm whitespace-nowrap"
                data-testid="nav-book-btn"
              >
                {t.nav.book}
                <ArrowUpRight size={16} />
              </a>
            </span>
            <button
              className="lg:hidden grid place-items-center h-10 w-10 rounded-lg glass"
              onClick={() => setOpen((v) => !v)}
              aria-label="Menu"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </nav>

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden mt-2 glass rounded-2xl p-4 flex flex-col gap-1" data-testid="mobile-menu">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="px-3 py-3 rounded-lg text-muted hover:text-white hover:bg-white/5 transition-colors"
              >
                {t.nav[l.key]}
              </a>
            ))}
            <div className="flex items-center justify-between mt-2 px-1">
              <LanguageToggle />
              <a
                href={`${BOOKING_PATH}?src=navbar`}
                className="btn-primary !py-2.5 !px-4 text-sm"
                onClick={() => { trackClick("book_time"); setOpen(false); }}
              >
                {t.nav.book} <ArrowUpRight size={15} />
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
